import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, Collapse, Paper, Stack, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { Meter, MeterInput, NewMeter } from '@vy/types';
import { MeterInputSchema } from '@vy/types';
import { AGGREGATION_OPERATORS, NewMeterSchema } from '@vy/types';
import { isNullOrUndefined, isObject } from '@vy/utils';
import React, { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { VyDialog } from '../../../components/new-design/feedback/VyDialog';
import { useCreateMeter } from '../../../hooks/requests/use-create-meter';
import { useUpdateMeter } from '../../../hooks/requests/use-update-meter';
import { queryKeys } from '../../../services/query-key/query-keys';
import { useDialog } from '../../../hooks/use-dialog';
import { useVyNavigate } from '../../../hooks/use-vy-navigate';
import { runMutation } from '../../../utils/vy-mutation';
import {
  invalidateMetersQueries,
  invalidateProductsQueries,
} from '../../../utils/query-invalidation';
import {
  buildNetSuiteIntegrationEntities,
  buildQuickBooksIntegrationEntities,
  buildXeroIntegrationEntities,
  buildAnrokIntegrationEntities,
} from '../ProductsPage/products-page-components/CreateProductDialog/UpsertCatalogProductsDialog/hooks/build-integration-entities';
import { CreateProductPageAppBar } from './create-product-page-components/CreateProductPageAppBar';
import { CreateProductPageForm } from './create-product-page-components/CreateProductPageForm';
import { ProductDescription } from './create-product-page-components/ProductDescription';
import { useToggle } from '../../../hooks/use-toggle';
import { MeterErpSection, type MeterErpSectionHandle } from './create-product-page-components/MeterErpSection';

export const CreateProductPage: React.FC = () => {
  const navigate = useVyNavigate();
  const location = useLocation();
  const productName = location.state?.name;
  const [isDescriptionOpen, toggleDescriptionVisibility] = useToggle(true);
  const erpSectionRef = useRef<MeterErpSectionHandle>(null);

  const {
    isDialogOpen: isUpdateMeterDialogOpen,
    handleOpen: handleOpenUpdateMeterDialog,
    handleClose: handleCloseUpdateMeterDialog,
  } = useDialog();

  const queryClient = useQueryClient();

  const refreshProductPage = async () => {
    await Promise.all([
      queryClient.invalidateQueries(queryKeys.productsPage()),
      invalidateMetersQueries(queryClient),
      invalidateProductsQueries(queryClient),
    ]);
  };

  const createMeterMutation = useCreateMeter({
    onSuccess: refreshProductPage,
  });
  const updateMeterMutation = useUpdateMeter({
    onSuccess: refreshProductPage,
  });

  const methods = useForm<MeterInput, unknown, NewMeter>({
    resolver: zodResolver(NewMeterSchema),
    defaultValues: isMeterInput(location.state)
      ? location.state
      : {
          name: productName,
          eventName: '',
          aggregationMethod: {
            operator: AGGREGATION_OPERATORS.Count,
          },
          creditTypeIds: [],
        },
  });

  const handleSubmit = methods.handleSubmit(async (newMeter) => {
    const isEditMode = doesMeterHaveId(location.state);

    const integrationEntities = isEditMode
      ? erpSectionRef.current?.getIntegrationEntities() ?? []
      : [
          ...buildNetSuiteIntegrationEntities(location.state?.netSuiteProduct, location.state?.netSuiteClass),
          ...buildQuickBooksIntegrationEntities(location.state?.quickBooksProduct),
          ...buildXeroIntegrationEntities(location.state?.xeroProduct),
          ...buildAnrokIntegrationEntities(location.state?.anrokProduct),
        ];

    const meterWithEntities = {
      ...newMeter,
      integrationEntities: integrationEntities.length > 0 ? integrationEntities : undefined,
    };

    if (doesMeterHaveId(location.state)) {
      await runMutation(
        updateMeterMutation,
        {
          id: location.state.id,
          ...meterWithEntities,
        },
        {
          pending: 'Updating product...',
          success: 'Product updated',
          error: 'Failed to update product',
        },
      );
    } else {
      await runMutation(createMeterMutation, meterWithEntities, {
        pending: 'Creating product...',
        success: 'Product created',
        error: 'Failed to create product',
      });
    }

    navigate('/products', {
      replace: true,
    });
  });

  if (isNullOrUndefined(productName)) {
    return <Navigate to="/products" />;
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: '#FAFAFA',
        overflowY: 'hidden',
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <CreateProductPageAppBar
            isDescriptionOpen={isDescriptionOpen}
            toggleDescriptionVisibility={toggleDescriptionVisibility}
          />

          <Box
            sx={{
              height: 'calc(100vh - 133px)',
              width: '100%',
              overflowY: 'hidden',
              display: 'flex',
            }}
          >
            <Stack
              sx={{
                textAlign: "center",
                rowGap: "24px",
                height: '100%',
                width: '100%',
                maxWidth: 'calc(100% - 354px)',
                padding: '40px 56px',
                overflowY: 'auto'
              }}>
              <Stack
                sx={{
                  rowGap: 2,
                  width: 1
                }}>
                <Typography variant="h5">Create Product</Typography>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Start by selecting the event you want to track.
                  <br />
                  This will help set the conditions and managing actions in your workflow.
                </Typography>
              </Stack>

              <Paper
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  padding: '24px',
                  borderRadius: '14px',
                })}
              >
                <CreateProductPageForm />
              </Paper>

              {doesMeterHaveId(location.state) && (
                <MeterErpSection ref={erpSectionRef} meterName={productName} />
              )}
            </Stack>

            <Collapse
              in={isDescriptionOpen}
              orientation="horizontal"
              sx={{
                height: '100%',
              }}
            >
              <ProductDescription toggleDescriptionVisibility={toggleDescriptionVisibility} />
            </Collapse>
          </Box>

          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              zIndex: 1000,
              justifyContent: 'end',
              textAlign: 'right',
              background: '#fff',
              height: '68px',
              padding: '16px',
              borderTop: '1px solid #E0E0E0',
            }}
          >
            <VyDialog
              open={isUpdateMeterDialogOpen}
              onClose={handleCloseUpdateMeterDialog}
              component="form"
              label={"You're about to update this product"}
              ctaText={
                <>
                  Save Product
                  <VyIcon
                    icon="ArrowSmallRight"
                    style={{
                      marginRight: '-6px',
                    }}
                  />
                </>
              }
              ctaButtonProps={{
                type: 'submit',
                disabled: methods.formState.isSubmitting,
                sx: {
                  fontWeight: 400,
                  columnGap: 1,
                },
              }}
            >
              <Typography>Are you sure you want to update this product?</Typography>
            </VyDialog>

            <Button
              variant="contained"
              color="primary"
              size="medium"
              sx={{
                fontWeight: 400,
                columnGap: 1,
              }}
              disabled={!methods.formState.isValid || methods.formState.isSubmitting}
              type={doesMeterHaveId(location.state) ? 'button' : 'submit'}
              onClick={doesMeterHaveId(location.state) ? handleOpenUpdateMeterDialog : undefined}
            >
              Save Product
              <VyIcon
                icon="ArrowSmallRight"
                style={{
                  marginRight: '-6px',
                }}
              />
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  );
};

function isMeterInput(meter: unknown): meter is MeterInput {
  return MeterInputSchema.safeParse(meter).success;
}

function doesMeterHaveId(meter: unknown): meter is { id: string } {
  return isObject(meter) && ('id' satisfies keyof Meter) in meter;
}
